import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { BtnLoader } from "src/Loader";
import { useAuth } from "src/contexts/auth-provider";
import { ReportPageLayout } from "src/features/Reports/components/ReportPageLayout";
import { useAxiosInterceptor } from "src/hooks/useAxiosInterceptor";
import { failureToast, successToast } from "src/utils/toasts";
import { useRestaurantFromParams } from "src/utils/useRestaurantFromParams";
import {
  pciComplianceUrl,
  type FinixComplianceForm,
  type FinixComplianceFormsResponse,
} from "./financeApi";

const cardClass = "rounded-xl border border-[#F1F1F2] bg-white p-4 sm:p-5 shadow-[0_4px_12px_rgba(0,0,0,0.04)]";
const consentText = "By submitting this Self-Assessment Questionnaire, I certify that I am an authorized representative of the company and that all the information submitted is true and correct.";

const statusClasses: Record<string, string> = {
  INCOMPLETE: "border-amber-200 bg-amber-50 text-amber-800",
  OVERDUE: "border-red-200 bg-red-50 text-red-700",
  COMPLETE: "border-emerald-200 bg-emerald-50 text-emerald-700",
  COMPLETED: "border-emerald-200 bg-emerald-50 text-emerald-700",
  EXPIRED: "border-slate-200 bg-slate-100 text-slate-700",
  INVALID: "border-red-200 bg-red-50 text-red-700",
};

export function FinancePciCompliancePage() {
  const { axBe } = useAxiosInterceptor();
  const { hasRestaurant } = useAuth();
  const { redirectTo } = useRestaurantFromParams();
  const storeCorrelationId = hasRestaurant?.correlationId;
  const [data, setData] = useState<FinixComplianceFormsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedFormId, setSelectedFormId] = useState<string | null>(null);
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [documentLoading, setDocumentLoading] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const loadForms = useCallback(() => {
    if (!storeCorrelationId) return Promise.resolve();
    setLoading(true);
    setError(null);
    return axBe
      .get<FinixComplianceFormsResponse>(pciComplianceUrl(storeCorrelationId))
      .then((response) => {
        setData(response.data);
        setSelectedFormId((current) => current ?? response.data.forms[0]?.id ?? null);
      })
      .catch(() => setError("Unable to load PCI compliance information."))
      .finally(() => setLoading(false));
  }, [axBe, storeCorrelationId]);

  useEffect(() => { void loadForms(); }, [loadForms]);
  useEffect(() => () => { if (documentUrl) URL.revokeObjectURL(documentUrl); }, [documentUrl]);

  const selectedForm = useMemo(
    () => data?.forms.find((form) => form.id === selectedFormId) ?? data?.forms[0] ?? null,
    [data, selectedFormId],
  );

  useEffect(() => {
    setReviewed(false);
    setAccepted(false);
    setName("");
    setTitle("");
    setDocumentUrl(null);
  }, [selectedFormId]);

  const openDocument = async (form: FinixComplianceForm, signed = false) => {
    if (!storeCorrelationId) return;
    setDocumentLoading(true);
    try {
      const suffix = `/forms/${encodeURIComponent(form.id)}/${signed ? "signed-document" : "document"}`;
      const response = await axBe.get<Blob>(pciComplianceUrl(storeCorrelationId, suffix), { responseType: "blob" });
      if (documentUrl) URL.revokeObjectURL(documentUrl);
      setDocumentUrl(URL.createObjectURL(response.data));
      if (!signed) setReviewed(true);
    } catch {
      failureToast("Unable to open the PCI compliance document.");
    } finally {
      setDocumentLoading(false);
    }
  };

  const attest = async () => {
    if (!storeCorrelationId || !selectedForm || !reviewed || !accepted || !name.trim() || !title.trim()) return;
    setSubmitting(true);
    try {
      await axBe.post(
        pciComplianceUrl(storeCorrelationId, `/forms/${encodeURIComponent(selectedForm.id)}/attest`),
        { name: name.trim(), title: title.trim(), certificationAccepted: accepted },
      );
      successToast("PCI compliance form submitted successfully.");
      setDocumentUrl(null);
      await loadForms();
    } catch {
      failureToast("Unable to submit the PCI compliance form.");
    } finally {
      setSubmitting(false);
    }
  };

  if (redirectTo) return <Navigate to={redirectTo} replace />;
  if (!storeCorrelationId) return <ReportPageLayout hideTitle>Select a restaurant to view PCI compliance.</ReportPageLayout>;

  return (
    <ReportPageLayout hideTitle error={error ?? undefined}>
      <div className="w-full max-w-[980px] space-y-4">
        <div className={cardClass}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-[#111827]">PCI Compliance</h2>
              <p className="mt-1 max-w-2xl text-sm text-[#6b7280]">
                Review and attest to the Finix-generated PCI Self-Assessment Questionnaire for this restaurant.
              </p>
            </div>
            <button type="button" onClick={() => void loadForms()} disabled={loading} className="h-10 w-full rounded-lg border border-[#e5e7eb] bg-white px-4 text-sm font-medium text-[#111827] disabled:opacity-60 sm:w-auto">
              Refresh
            </button>
          </div>
        </div>

        {loading && !data ? (
          <div className={`${cardClass} flex min-h-[240px] items-center justify-center`}><BtnLoader /></div>
        ) : !data?.paymentAccountConnected ? (
          <div className={cardClass}><p className="text-sm text-[#6b7280]">Connect and approve the restaurant&apos;s Finix payment account before managing PCI compliance.</p></div>
        ) : !selectedForm ? (
          <div className={cardClass}><p className="text-sm text-[#6b7280]">Finix has not generated a PCI compliance form for this merchant yet.</p></div>
        ) : (
          <>
            <div className={cardClass}>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-base font-semibold text-[#111827]">SAQ A {selectedForm.version ? `· ${selectedForm.version}` : ""}</h3>
                    <StatusBadge form={selectedForm} />
                  </div>
                  <dl className="mt-4 grid grid-cols-1 gap-x-8 gap-y-3 text-sm sm:grid-cols-3">
                    <DateDetail label="Due" value={selectedForm.dueAt} />
                    <DateDetail label="Valid from" value={selectedForm.validFrom} />
                    <DateDetail label="Valid until" value={selectedForm.validUntil} />
                  </dl>
                </div>
              </div>

              {data.forms.length > 1 && (
                <div className="mt-5 border-t border-[#e5e7eb] pt-4">
                  <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#6b7280]">Compliance history</p>
                  <div className="flex flex-wrap gap-2">
                    {data.forms.map((form, index) => (
                      <button key={form.id} type="button" onClick={() => setSelectedFormId(form.id)} className={`rounded-lg border px-3 py-2 text-left text-xs font-medium ${selectedForm.id === form.id ? "border-[#FD7657] bg-[#fff7f3] text-[#b9381b]" : "border-[#e5e7eb] bg-white text-[#374151]"}`}>
                        {index === 0 ? "Current" : `Previous ${index}`} · {form.displayState}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-5 flex flex-col gap-2 sm:flex-row">
                {selectedForm.unsignedDocumentAvailable && (
                  <button type="button" onClick={() => void openDocument(selectedForm)} disabled={documentLoading} className="h-10 rounded-lg bg-[#FD7657] px-4 text-sm font-medium text-white disabled:opacity-60">
                    {documentLoading ? "Opening..." : "Review compliance form"}
                  </button>
                )}
                {selectedForm.signedDocumentAvailable && (
                  <button type="button" onClick={() => void openDocument(selectedForm, true)} disabled={documentLoading} className="h-10 rounded-lg border border-[#e5e7eb] bg-white px-4 text-sm font-medium text-[#111827] disabled:opacity-60">
                    View signed form
                  </button>
                )}
              </div>
            </div>

            {documentUrl && (
              <div className={cardClass}>
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-base font-semibold text-[#111827]">Compliance document</h3>
                  <a href={documentUrl} download="pci-compliance.pdf" className="text-sm font-medium text-[#FD7657]">Download PDF</a>
                </div>
                <iframe title="PCI compliance document" src={documentUrl} className="h-[68vh] min-h-[420px] w-full rounded-lg border border-[#e5e7eb]" />
              </div>
            )}

            {selectedForm.canAttest && (
              <div className={cardClass}>
                <h3 className="text-base font-semibold text-[#111827]">Merchant attestation</h3>
                <p className="mt-1 text-sm text-[#6b7280]">Review the complete document before submitting this legal certification.</p>
                <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <label className="text-sm font-medium text-[#374151]">Full legal name
                    <input value={name} onChange={(event) => setName(event.target.value)} maxLength={255} className="mt-1 h-11 w-full rounded-lg border border-[#d1d5db] px-3 font-normal text-[#111827]" />
                  </label>
                  <label className="text-sm font-medium text-[#374151]">Job title
                    <input value={title} onChange={(event) => setTitle(event.target.value)} maxLength={255} className="mt-1 h-11 w-full rounded-lg border border-[#d1d5db] px-3 font-normal text-[#111827]" />
                  </label>
                </div>
                <label className={`mt-5 flex items-start gap-2 ${reviewed ? "cursor-pointer" : "cursor-default opacity-60"}`}>
                  <input type="checkbox" checked={accepted} onChange={(event) => setAccepted(event.target.checked)} disabled={!reviewed} className="receipt-lang-check mt-0.5 h-6 min-h-[24px] w-6 min-w-[24px] shrink-0 appearance-none rounded border border-[#9B9B9B] cursor-pointer disabled:cursor-default" />
                  <span className="text-sm text-[#374151]">{consentText}</span>
                </label>
                {!reviewed && <p className="mt-2 text-xs text-[#6b7280]">Open the unsigned compliance form before certifying it.</p>}
                <button type="button" onClick={() => void attest()} disabled={submitting || !reviewed || !accepted || !name.trim() || !title.trim()} className="mt-5 h-11 w-full rounded-lg bg-[#FD7657] px-5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto">
                  {submitting ? "Submitting..." : "Attest and submit"}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </ReportPageLayout>
  );
}

function StatusBadge({ form }: { form: FinixComplianceForm }) {
  const key = (form.state ?? "UNKNOWN").toUpperCase();
  return <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${statusClasses[key] ?? "border-slate-200 bg-slate-100 text-slate-700"}`}>{form.displayState}</span>;
}

function DateDetail({ label, value }: { label: string; value?: string | null }) {
  return <div><dt className="text-xs text-[#6b7280]">{label}</dt><dd className="mt-1 font-medium text-[#111827]">{value ? new Date(value).toLocaleDateString() : "—"}</dd></div>;
}
